<template>
  <div
    id="side-nav"
    class="portal-nav"
    :style="appStyleProps"
    :class="{ 'fixed-header': !iframeHeaderVisible }">
    <div data-cy="portal-side-nav" class="portal-nav-container">
      <NuxtImg v-if="!iframeHeaderVisible && !!appNavLogo"
          :src="formatImagePath(appNavLogo)"
          alt=""
          class="affiliate-customizations-app-nav-logo top-nav-logo"
          :class="{ 'mkpl-logo': !isTenantInstance }"
      />
      <div class="d-lg-none">
        <BankPullStatusChip dark />
      </div>

      <div
        class="d-lg-none"
        v-click-outside="closeSideNav">
        <div data-cy="portal-hamburger-nav"
          @click="toggleNav()"
          class="hamburger-menu"
          :class="{ 'is-tenant': isTenantInstance }">
          <span class="line"></span>
          <span class="line"></span>
          <span class="line"></span>
        </div>

        <NavMenu v-show="dropdownVisible"
          :position="{
            position: 'fixed',
            right: '20px',
            top: 'calc(80px + var(--portal-banner-height, 0px))'
          }"
          @close="dropdownVisible = false" />
      </div>

      <div class="d-none d-lg-flex flex-column nav-wrap me-2 m-lg-0" :class="{ 'tenant-layout': isTenantInstance }">
        <ul>
          <li v-for="link in links"
            :key="kebabString(link.title)"
            :class="{ 'd-none': link.isHidden && !checkCurrentPath(link.url) }"
            @mouseover='linkHover=link.title'
            @mouseleave='linkHover=""'>
            <router-link
              :to="link.url"
              class="m-4 d-block align-middle"
              :class="{ 'current': checkCurrentPath(link.url) }"
              :data-cy="'side-nav-' + kebabString(link.title)">
              <span v-if="link.imgIcon" :class="{ 'info-badge': isMounted && link.infoBadge }">
                <NuxtImg class="align-middle mb-1 me-2" v-if="(((linkHover === link.title) || (checkCurrentPath(link.url)) && !!link.iconActive)) && link.iconActive !== link.faIcon" :src="formatImagePath(link.iconActive)" />
                <NuxtImg class="align-middle mb-1 me-2" v-else :src="formatImagePath(link.imgIcon)" />
              </span>
              <span class="me-1 d-inline-block text-center" :class="{ 'info-badge': isMounted && link.infoBadge }" v-else><font-awesome-icon :icon="falIconMap[link.faIcon]" class="fa-fw"></font-awesome-icon></span>
              {{ link.title }}
              <span v-if="link.postText" style="margin-left: 5px; background-color: #FFDD00; font-weight: 400; font-size: .8em; padding: 0px 7px; letter-spacing: 1px;">{{link.postText}}</span>
            </router-link>
          </li>
        </ul>
        <div class="d-none d-lg-block mt-auto">
          <client-only>
            <SidenavProfileDropdown v-if="!isEmbedded" />
            <!-- Deliberately a sibling of SidenavProfileDropdown, not nested inside its slot -
                 that component's mouseenter/mouseleave pair controls its own "Sign Out" menu, and
                 mousing over a nested chip would never fire its mouseleave (the chip is still a
                 descendant), keeping that menu open and overlapping the chip's own popover. -->
            <!-- tw:pl-8 (2rem/32px) left-aligns the bank icon under the profile icon above. This
                 chip sits outside SidenavProfileDropdown's markup entirely (see comment above), so
                 none of that component's own left insets apply here - they have to be reproduced:
                 .profile-dropdown's 8px + .portal-username's 8px + .profile-link's px-4 (confirmed
                 via the compiled @lendiodevs/lendio-styleguide CSS to be 1rem/16px !important, on
                 a Tailwind-identical 4px-per-unit scale - NOT Bootstrap's own default 1.5rem/24px
                 px-4, which this styleguide's utilities do not match) = 32px total. -->
            <!-- tw:empty:p-0: this wrapper is always rendered, but the chip inside it is not - a
                 hidden chip (anyone outside the bank-pull-status experiment, or with nothing to
                 report) is just Vue's v-if comment node, which :empty does not count as content.
                 Without this the pb-2 below still added 8px between the profile dropdown and the
                 support block for every such borrower. -->
            <div class="tw:pl-8 tw:pb-2 tw:empty:p-0">
              <BankPullStatusChip dark />
            </div>
            <div v-if="isTenantInstance">
              <SupportInfo v-if="showTenantSupportInfo" />
              <PoweredByLendio v-if="showPoweredByLendio" />
            </div>
            <div v-else>
              <SupportInfo />
              <TrustpilotWidget :height='85' :width='100' theme='dark' class="px-5 pb-5" />
            </div>
          </client-only>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { PORTAL_NAV_URLS } from "~/libs/portal-constants"
import {
  faBorderAll,
  faFileInvoiceDollar,
  faFolder,
  faHouse,
  faSackDollar,
  faUserGear,
} from '@fortawesome/pro-light-svg-icons'

const falIconMap = {
  'border-all': faBorderAll,
  'file-invoice-dollar': faFileInvoiceDollar,
  'folder': faFolder,
  'house': faHouse,
  'sack-dollar': faSackDollar,
  'user-gear': faUserGear,
}
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization';
import { useBorrowerStore } from '~/store/borrower'
import { usePortalStore } from '~/store/portal'
import { useRootStore } from '~/store/root'
import formatImagePath from '~/libs/image-format'
import NavMenu from '~/components/NavMenu.vue';
import PoweredByLendio from '~/components/PoweredByLendio.vue'
import SidenavProfileDropdown from '~/components/portal/SidenavProfileDropdown.vue'
import SupportInfo from '~/components/SupportInfo.vue'
import TrustpilotWidget from '~/components/TrustpilotWidget.vue'
import BankPullStatusChip from '~/components/bank-pull-status/BankPullStatusChip.vue'

export default {
  name: 'NewPortalSideNav',
  async setup () {
    return {
      falIconMap,
      formatImagePath,
    }
  },
  data () {
    return {
      dropdownVisible: false,
      linkHover: '',
      isMounted: false,
    }
  },
  components: {
    SidenavProfileDropdown,
    SupportInfo,
    PoweredByLendio,
    NavMenu,
    TrustpilotWidget,
    BankPullStatusChip,
  },
  computed: {
    ...mapState(usePortalStore, ['navigationItems', 'settingsNavigationItem']),
    ...mapState(useBorrowerStore, ['borrower']),
    ...mapState(useRootStore, ['isEmbedded']),
    ...mapState(useAffiliateCustomizationStore, [
      'affiliateCustomizations',
      'appNavLogo',
      'appStyleProps',
      'contactInfoEnabled',
      'iframeHeaderVisible',
      'isEmbeddedLayout',
      'poweredByLendioEnabled',
    ]),
    isTenantInstance() {
      return this.isEmbeddedLayout
    },
    showTenantSupportInfo() {
      return !!this.affiliateCustomizations && this.contactInfoEnabled
    },
    showPoweredByLendio() {
      return !!this.affiliateCustomizations && this.poweredByLendioEnabled
    },
    links() {
      return this.navigationItems.filter(l => !!l && l.title !== this.settingsNavigationItem?.title)
    },
  },
  watch: {
    '$route.path' () {
      this.dropdownVisible = false
    },
  },
  mounted() {
    this.isMounted = true
  },
  methods: {
    toggleNav() {
      this.dropdownVisible = !this.dropdownVisible
    },
    closeSideNav() {
      this.dropdownVisible = false
    },
    kebabString(str) {
      return str.replace(/\s/g , "-").toLowerCase()
    },
    checkCurrentPath(linkUrl) {
      return linkUrl === PORTAL_NAV_URLS.OFFERS
        ? this.$route.path.includes(linkUrl)
        : this.$route.path === linkUrl
    },
  },
}
</script>

<style scoped lang="scss">
@import 'assets/styles/affiliate-customizations/affiliate-customization-mixins';
@import 'assets/styles/affiliate-customizations/affiliate-customizations-portal-sidenav';

#side-nav {
  max-width: 300px;
  z-index: 100;
  position: relative !important;
  border-right: 1px #cbcdce solid;
  @include affiliateCustomizedPortalSidenav();
  flex-shrink: 0;
  width: min(25%, 300px);

  .portal-nav-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
  }

  .affiliate-customizations-app-nav-logo {
    @include affiliateAppNavLogoPortal();
    max-width: min(210px, 100% - 64px);
    &.mkpl-logo {
      width: 110px;
    }
    @include media-breakpoint-down(lg) {
      &.top-nav-logo {
        margin: 12px 32px 12px 32px;
        max-height: 52px;
        max-width: min(210px, 100% - 94px);
      }
    }
  }
  @include media-breakpoint-down(lg) {
    width: 68px;
    max-width: none;
    position: absolute !important;
    top:0;
    right: 0;
    margin-top: 0;
    height: 76px;
    border-right: none;

    &.fixed-header {
      width: 100%;
      position: fixed !important;
      border-bottom: 1px #cbcdce solid;
      top: var(--portal-banner-height, 0px);
    }

    .portal-nav-container {
      align-items: center;
      flex-direction: row;
    }
  }
  @include media-breakpoint-up(lg) {
    .portal-nav-container {
      top: var(--portal-banner-height, 0px);
      position: sticky;
      max-height: calc(100vh - var(--portal-banner-height, 0px));
      overflow-y: auto;
    }
  }
  .nav-wrap {
    @include media-breakpoint-up(lg) {
      flex-grow: 1;
    }
    @include media-breakpoint-down(lg) {
      display: none;
    }
    ul {
      padding: 8px 0;
      margin: 0;
      list-style: none;
    }
  }
  @include media-breakpoint-down(lg) {
    &.fixed-header .nav-wrap {
      top: 62px;
    }
  }
  li {
    a {
      padding: 8px 16px;
      border-radius: 8px;
      color: $background-100;
      font-size: 16px;
      white-space: nowrap;
      &:hover, &.current {
        text-decoration: none;
      }
      &:hover {
        color: $type-600;
        background: $background-100;

        .info-badge::after {
          border-color: $background-100;
        }
      }
      &.current {
        font-weight: bold;
        color: $black;
        background: $background-100;
        svg {
          color: $black;
        }
        .info-badge::after {
          border-color: $background-100;
        }
      }
      img {
        display:inline-block;
        margin-right: 10px;
        width: 18px;
      }
    }
  }
  .tenant-layout {
    li {
      a {
        color: $app-nav-text-primary !important;
        &:hover {
          color: $app-body-primary-font-color !important;
          background: $app-body-background-color;

          .info-badge::after {
            border-color: $app-body-background-color;
          }
        }
        &.current {
          color: $app-card-label-font-color !important;
          background: $app-button-background-color;
          svg {
            color: $app-card-label-font-color;
          }
          .info-badge::after {
            border-color: $app-button-background-color;
          }
        }
      }
    }
  }

  .hamburger-menu {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 35px;
    cursor: pointer;
    padding: 10px;
    position: absolute;
    top: 22px;
    right: 15px;
    .line {
      display: block;
      width: 15px;
      height: 2px;
      background-color: $app-nav-text-primary;
      margin-bottom: 3px;
      border-radius: 2px;
    }
  }
  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .info-badge {
    position: relative;

    &::after {
      top: 0;
      right: 0;
      content: "";
      position: absolute;
      background-color: $green-300;
      border: 2px solid $app-aside-background-color;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      transform: translate(1px, -1px);
    }
  }
}
</style>
